/**
 * A run's bar (AMO-306): one row under the header, never over the chart.
 * Its status, a progress bar split per step (a segment opens its step), how
 * many steps are done, who started it and when (by name), what it ran with,
 * and the one thing to do next: Stop, Retry the failed step, whose turn it
 * is at a waiting step, or Run again. An approver's partial view (`sight`)
 * has no status of the run, its starter's failure or Stop to show: only where
 * their own step stands.
 *
 * @module components/workflows/RunBar
 */
import type { WorkflowRun, WorkflowStepStatus } from "@amoreg/contracts";
import { PlayIcon, RotateCcwIcon, SquareIcon } from "lucide-react";

import { cn } from "~/lib/utils";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import { Button } from "../ui/button";
import { peopleLabel, runMainAction, runProgressLabel, runSegments } from "./RunBar.logic";
import { RunStatusPill } from "./workflowParts";
import { PARTIAL_RUN_LINE, partialRunStanding, stepStatusLabel } from "./workflowCanvas.logic";

const SEGMENT: Readonly<Record<WorkflowStepStatus, string>> = {
  pending: "bg-muted",
  running: "bg-info animate-pulse",
  waiting: "bg-warning",
  succeeded: "bg-success",
  failed: "bg-destructive",
  skipped: "bg-muted-foreground/25",
  cancelled: "bg-muted-foreground/40",
};

export function RunBar({
  run,
  nameOf,
  viewerEmail,
  mayRetry,
  retrying,
  onStop,
  onRetry,
  onRunAgain,
  runAgainBlocked = null,
  onOpenStep,
}: {
  /** Why Run again cannot start now (unsaved edits, problems on the chart), as the header's Run says; null when it can. */
  readonly runAgainBlocked?: string | null;
  readonly run: WorkflowRun;
  readonly nameOf: (email: string) => string | null;
  readonly viewerEmail: string | null | undefined;
  readonly mayRetry: boolean;
  readonly retrying: boolean;
  /** Null for a reader the server would not let stop it. */
  readonly onStop: (() => void) | null;
  readonly onRetry: (nodeId: string) => void;
  readonly onRunAgain: () => void;
  readonly onOpenStep: (nodeId: string) => void;
}) {
  const segments = runSegments(run);
  const action = runMainAction(run, { email: viewerEmail, mayRetry });
  const starter = run.startedByEmail ? (nameOf(run.startedByEmail) ?? run.startedByEmail) : null;
  const partial = run.sight === "approver" ? partialRunStanding(run, viewerEmail) : null;
  const active = partial === null && (run.status === "queued" || run.status === "running" || run.status === "waiting");
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-border bg-muted/20 px-3 py-2 sm:px-4" data-workflow-run-bar="">
      {partial === null ? (
        <RunStatusPill status={run.status} />
      ) : (
        <span className="text-xs" data-workflow-run-partial={partial}>
          {PARTIAL_RUN_LINE[partial]}
        </span>
      )}
      <div className="flex min-w-40 flex-1 items-center gap-2">
        <div className="flex h-2 min-w-24 flex-1 gap-0.5 overflow-hidden rounded-full" role="list" aria-label="Steps">
          {segments.map((segment) => (
            <button
              key={segment.nodeId}
              type="button"
              role="listitem"
              title={`${segment.label}: ${stepStatusLabel(segment.status, segment.type)}`}
              aria-label={`${segment.label}: ${stepStatusLabel(segment.status, segment.type)}`}
              onClick={() => onOpenStep(segment.nodeId)}
              className={cn("h-full flex-1 transition-opacity hover:opacity-70", SEGMENT[segment.status])}
              data-workflow-run-segment={segment.status}
            />
          ))}
        </div>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground" data-workflow-run-progress="">
          {runProgressLabel(segments)}
        </span>
      </div>
      <span className="text-xs text-muted-foreground">
        Started {formatRelativeTimeLabel(run.createdAt)}
        {starter ? ` by ${starter}` : ""}
      </span>
      {run.input ? (
        <span className="max-w-56 truncate text-xs text-muted-foreground" title={run.input} data-workflow-run-input="">
          · {run.input}
        </span>
      ) : null}
      {run.error ? <span className="max-w-72 truncate text-xs text-destructive" title={run.error}>· {run.error}</span> : null}
      <div className="ms-auto flex items-center gap-1.5">
        {action.kind === "waiting" ? (
          action.mine ? (
            <Button size="xs" onClick={() => onOpenStep(action.nodeId)} data-workflow-run-action="yours">
              {action.checklist ? "Your turn: mark it done" : "Your approval"}
            </Button>
          ) : (
            <button
              type="button"
              className="text-xs text-warning-foreground underline-offset-2 hover:underline"
              onClick={() => onOpenStep(action.nodeId)}
              data-workflow-run-action="waiting"
            >
              Waiting on {peopleLabel(action.people, nameOf)}
              {action.checklist ? " to do" : "'s approval at"} “{action.label}”
            </button>
          )
        ) : null}
        {action.kind === "retry" ? (
          <Button size="xs" onClick={() => onRetry(action.nodeId)} disabled={retrying} data-workflow-run-action="retry">
            <RotateCcwIcon />
            {retrying ? "Retrying…" : `Retry “${action.label}”`}
          </Button>
        ) : null}
        {action.kind === "again" ? (
          <Button
            size="xs"
            variant="outline"
            onClick={onRunAgain}
            disabled={runAgainBlocked !== null}
            title={runAgainBlocked ?? undefined}
            data-workflow-run-action="again"
          >
            <PlayIcon />
            Run again
          </Button>
        ) : null}
        {onStop && active ? (
          <Button size="xs" variant="outline" onClick={onStop} data-workflow-stop="">
            <SquareIcon />
            Stop
          </Button>
        ) : null}
      </div>
    </div>
  );
}
